/**
 * **병사의 레벨 성장이 얼마나 잘 붙었는가**(능력치 화면). 레벨이 오를 때마다 체력은 2·3·4%, 공격은 1.4·2.4·3.4% 가운데
 * 하나가 붙는다(`growthRoll`, troops.ts — 평균은 3%·2.4%). 처음에는 "체력 +181% · 공격 +150.4% (보통 +183% · +146.4%)"
 * 한 줄로만 적었다 — 네 숫자를 암산으로 견줘야 운이 좋았는지 알았다. 플레이한 사람이 청했다(2026-10-07): "얼마나 잘
 * 성장한건지 한 눈에 파악할 수 있게".
 *
 * 등급과 "상위 몇 %"로 적는다. 레벨마다의 굴림이 세 값 가운데 하나(−1·0·+1 %p, 고르게)이므로 `n`번 굴린 합의 흩어짐은
 * √(n × 2/3) %p다 — 합이 평균에서 그 몇 배 떨어졌는가(z)를 정규 분포에 대어 순위를 낸다. 레벨이 낮으면(굴림이 적으면)
 * 어림이 거칠다 — 화면에만 쓰는 값이다(전투·정산은 이 등급을 모른다). 그래서 여기서는 부동소수점을 써도 된다.
 */
import { SOLDIER_LEVEL_ATK_PERMILLE, SOLDIER_LEVEL_HP_PERMILLE } from '../sim/troops.ts';
import type { Growth } from '../sim/troops.ts';

/** 한 레벨의 굴림이 평균에서 벌어지는 폭(천분율 포인트) — `growthRoll`의 ±10. */
const ROLL_STEP = 10;

export type GrowthTone = 'top' | 'good' | 'even' | 'low' | 'poor';

export interface GrowthStat {
  /** 쌓인 몫(천분율 포인트)과 같은 레벨의 평균. */
  readonly got: number;
  readonly mean: number;
  /** 평균에서 벌어진 정도(흩어짐의 몇 배). 양수가 좋다. */
  readonly z: number;
  /** 막대 위의 자리(0~100, 50이 평균 — ±3배에서 끝에 닿는다). */
  readonly at: number;
}

export interface GrowthGrade {
  readonly hp: GrowthStat;
  readonly atk: GrowthStat;
  /** 체력과 공격을 합친 정도. */
  readonly z: number;
  readonly tone: GrowthTone;
  /** "뛰어남"·"좋음"·"보통"·"아쉬움"·"나쁨". */
  readonly label: string;
  /** 같은 레벨의 병사 백 명 가운데 몇 번째쯤인가(1~99, 작을수록 좋다). */
  readonly topPct: number;
}

/** 표준 정규 분포에서 z 이하일 확률(Abramowitz–Stegun 7.1.26 — 화면용 어림, 오차 1e-7 안팎). */
function normalCdf(z: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(z) * Math.SQRT1_2);
  const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  const erf = 1 - poly * Math.exp(-(z * z) / 2);
  return z >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

/** 굴린 횟수가 이보다 적으면 순위(상위 N%)를 적지 않는다 — 같은 값인 병사가 많아 정규 어림의 순위가 뜻이 없다. */
export const GRADE_RANK_FROM = 6;

/** 등급의 문턱(z): 상위 10%·30%, 하위 30%·10%. */
const TOP_Z = 1.2816;
const GOOD_Z = 0.5244;

const LABEL: Record<GrowthTone, string> = { top: '뛰어남', good: '좋음', even: '보통', low: '아쉬움', poor: '나쁨' };

function stat(got: number, perLevel: number, n: number): GrowthStat {
  const mean = perLevel * n;
  const sd = ROLL_STEP * Math.sqrt((n * 2) / 3);
  const z = sd > 0 ? (got - mean) / sd : 0;
  return { got, mean, z, at: Math.max(0, Math.min(100, 50 + (z * 50) / 3)) };
}

/** 레벨 `level`까지 `g`만큼 쌓은 병사의 성장 등급. 레벨 1(굴린 적 없음)은 null. */
export function growthGrade(g: Growth, level: number): GrowthGrade | null {
  const n = Math.floor(level) - 1;
  if (!(n > 0)) return null;
  const hp = stat(g.hp, SOLDIER_LEVEL_HP_PERMILLE, n);
  const atk = stat(g.atk, SOLDIER_LEVEL_ATK_PERMILLE, n);
  // 두 굴림은 서로 무관하다 — 합친 정도는 둘의 합을 √2로 나눈 것이다.
  const z = (hp.z + atk.z) * Math.SQRT1_2;
  const tone: GrowthTone = z >= TOP_Z ? 'top' : z >= GOOD_Z ? 'good' : z > -GOOD_Z ? 'even' : z > -TOP_Z ? 'low' : 'poor';
  const topPct = Math.max(1, Math.min(99, Math.round((1 - normalCdf(z)) * 100)));
  return { hp, atk, z, tone, label: LABEL[tone], topPct };
}

/** 평균과의 차이를 글로: "+4%p", "−2%p", "같음". 천분율 포인트를 받는다. */
export function gapText(got: number, mean: number): string {
  const d = got - mean;
  if (d === 0) return '보통과 같음';
  const v = (Math.abs(d) / 10).toFixed(1).replace(/\.0$/, '');
  return `보통보다 ${d > 0 ? '+' : '−'}${v}%p`;
}
